import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { isEmptyPage } from '@/components/is-empty-file/isEmptyPage'
import { getCurrentPublicRuntimeConfig } from 'src/utils/helpers/helpers'

export type LocaleSlug = {
  [locale: string]: string
}

interface HrefLangLinksProps {
  hrefLangs: LocaleSlug
  brand?: Brand
}

export const renameLocale = {
  en: 'x-default',
  'nl-NL': 'nl',
  'de-DE': 'de',
}

/**
 * Resolves the value used for a locale's `hreflang` attribute:
 * a renamed value where one exists (e.g. `en` → `x-default`), otherwise the
 * lowercased locale (e.g. `en-GB` → `en-gb`).
 */
export const getHrefLangValue = (locale: string): string =>
  renameLocale[locale] || locale?.toLowerCase()

/**
 * Resolves the value used for schema.org `inLanguage`. Mirrors the hreflang
 * logic so structured data and alternate links stay aligned, but maps the
 * hreflang-only `x-default` back to `en` since it is not a valid language tag.
 */
export const getInLanguageFromLocale = (locale: string): string => {
  const value = getHrefLangValue(locale)
  return value === 'x-default' ? 'en' : value
}

export const HrefLangLinks = ({
  hrefLangs,
  brand = 'novasol',
}: HrefLangLinksProps): JSX.Element[] => {
  if (!hrefLangs) return

  if (hrefLangs['de-DE'] && hrefLangs['de']) {
    delete hrefLangs['de']
  }

  return (
    hrefLangs &&
    Object.entries(hrefLangs)?.map(([locale, link]) => {
      const localeBaseUrl =
        getCurrentPublicRuntimeConfig()?.[brand]?.localeBaseUrlMap?.[locale]

      return !isEmptyPage(link) && localeBaseUrl ? (
        <link
          key={locale}
          rel="alternate"
          data-testid="hreflang-link"
          hrefLang={getHrefLangValue(locale)}
          href={`${localeBaseUrl}${link === '/' ? '' : `/${link}`}`}
        />
      ) : null
    })
  )
}
